import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBookingDraft } from '../../contexts/BookingDraftContext';
import Header from '../../components/layout/Header';
import Icon from '../../components/AppIcon';

import FilterControls from './components/FilterControls';
import PhotographerResultCard from './components/PhotographerResultCard';
import PhotographerProfileModal from './components/PhotographerProfileModal';
import { photographerService } from '../../services/photographerService';
import { bookingService } from '../../services/bookingService';
import { locationService, calculateDistance } from '../../services/locationService';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import BackButton from '../../components/BackButton';

// SAFE MODE: Default service radius if not set (KM)
const DEFAULT_SERVICE_RADIUS_KM = 50;

// أيام الأسبوع بالعربية (0=الأحد، 1=الإثنين، ...)
const ARABIC_DAYS = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

// ربط الساعات بالفترات (صباح/ظهر/مساء) حسب التوفر الأسبوعي
const getPeriodForHour = (hour) => {
  if (hour >= 8 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour <= 22) return 'evening';
  return null;
};


const PhotographerResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { draft, updateDraft } = useBookingDraft();
  // تفضيل location.state عند التنقل من المعالج (أحدث بيانات) ثم draft
  const bookingData = location?.state?.bookingData || draft || {};

  const [sortBy, setSortBy] = useState('relevance');
  const [priceFilter, setPriceFilter] = useState({ min: 0, max: 10000 });
  const [ratingFilter, setRatingFilter] = useState(0);
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [photographers, setPhotographers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPhotographerId, setSelectedPhotographerId] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  // SAFE MODE: State for out-of-range modal
  const [showOutOfRangeModal, setShowOutOfRangeModal] = useState(false);
  const [outOfRangePhotographer, setOutOfRangePhotographer] = useState(null);
  const [calculatedDistance, setCalculatedDistance] = useState(null);

  useEffect(() => {
    loadPhotographers();
  }, []);

  // Load nearest photographers based on precise location
  const loadNearestPhotographers = async () => {
    try {
      const { lat, lng } = bookingData.preciseLocation;
      const category = mapPhotographyTypeToSpecialization(bookingData.photographyType);
      
      const nearest = await locationService.getNearestPhotographers({
        lat,
        lng,
        city: bookingData.city,
        category,
        limit: 10
      });

      if (nearest && nearest.length > 0) {
        // Merge with existing photographers and update distances
        setPhotographers(prev => {
          const updated = prev.map(p => {
            const nearestMatch = nearest.find(n => n.user_id === p.id || n.photographer_id === p.id);
            if (nearestMatch) {
              return {
                ...p,
                distanceKm: nearestMatch.distanceKm,
                distanceLabel: nearestMatch.distanceLabel,
                matchScore: (p.matchScore || 0) + (50 - nearestMatch.distanceKm) // Add distance bonus to match score
              };
            }
            return p;
          });
          return updated;
        });
      }
    } catch (err) {
      console.warn('Failed to load nearest photographers:', err);
      // Continue with regular loading
    }
  };

  const loadPhotographers = async () => {
    try {
      setLoading(true);
      setError('');
      // Skip cache when we have date+time to ensure fresh weekly_availability data for filtering
      const skipCache = !!(bookingData?.date || bookingData?.bookingDate) && !!(bookingData?.time || bookingData?.bookingTime);

      // جلب معرّفات المصورين المشغولين في تاريخ الحجز المختار (تجاوز RLS)
      const sessionDate = bookingData?.date || bookingData?.bookingDate || null;
      let busyPhotographerIds = new Set();
      if (sessionDate) {
        try {
          const { data: busyRows } = await supabase.rpc('get_busy_photographer_ids_on_date', {
            p_booking_date: sessionDate,
          });
          if (busyRows) {
            busyRows.forEach((row) => {
              if (row.photographer_id) busyPhotographerIds.add(row.photographer_id);
            });
          }
        } catch (_) {
          // non-blocking — proceed without date filtering if RPC fails
        }
      }

      const data = await photographerService?.getAllPhotographers(skipCache);
      // استبعاد المصورين الذين لديهم حجز مؤكد في تاريخ الحجز المختار
      const filteredData = busyPhotographerIds.size > 0
        ? (data || []).filter((p) => !busyPhotographerIds.has(p.user_id || p.userId || p.id))
        : data;
      
      console.log('📸 Loaded photographers:', {
        total: data?.length || 0,
        afterBusyFilter: filteredData?.length || 0,
        busyCount: busyPhotographerIds.size,
        sessionDate,
      });
      
      // Map city names to values for filtering
      const cityMap = {
        'الرياض': 'riyadh',
        'جدة': 'jeddah',
        'الدمام': 'dammam',
        'المدينة المنورة': 'medina',
        'مكة المكرمة': 'mecca',
        'الطائف': 'taif',
        'أبها': 'abha',
        'خميس مشيط': 'khamees_mushait',
        'جازان': 'jazan',
        'نجران': 'najran',
        'الباحة': 'baha',
        'تبوك': 'tabuk',
        'حائل': 'hail',
        'بريدة': 'buraydah',
        'سكاكا': 'sakaka',
      };

      // Helper function to get experience label from experience_level enum or calculate from years
      const getExperienceLabel = (level, years) => {
        // If experience_level enum exists, use it
        if (level) {
          const levelMap = {
            'certified': 'معتمد',
            'distinguished': 'مميز',
            'elite': 'نخبة',
            // Legacy mappings
            'beginner': 'معتمد',
            'intermediate': 'مميز',
            'advanced': 'مميز',
            'expert': 'نخبة'
          };
          return levelMap[level] || 'معتمد';
        }
        // Otherwise, calculate from years
        if (years >= 8) return 'نخبة';
        if (years >= 4) return 'مميز';
        return 'معتمد';
      };

      // Transform data to match existing structure
      const formattedPhotographers = filteredData?.map(p => {
        const city = p?.city || 'الرياض';
        const cityValue = cityMap[city] || city.toLowerCase().replace(/\s+/g, '_');
        // Get experience_years from database - check both camelCase and snake_case
        const experienceYears = p?.experienceYears ?? p?.experience_years ?? 0;
        // Get experience_level enum from database
        const experienceLevelEnum = p?.experienceLevel ?? p?.experience_level ?? null;
        
        // Calculate experience level label using enum first, then fallback to years
        const experienceLevelLabel = getExperienceLabel(experienceLevelEnum, experienceYears);
        
        // Debug: Log experience data for each photographer
        console.log('📊 Experience data for photographer:', {
          id: p?.userId || p?.user_id,
          name: p?.user?.fullName || p?.user?.full_name,
          experienceYears: p?.experienceYears,
          experience_years: p?.experience_years,
          experienceLevel: p?.experienceLevel,
          experience_level: p?.experience_level,
          calculatedExperienceYears: experienceYears,
          calculatedExperienceLevelLabel: experienceLevelLabel
        });
        
        // Get shooting methods - handle both camelCase and snake_case
        const shootingMethodsData = p?.shootingMethods || p?.shooting_methods || [];
        const shootingMethodsArray = Array.isArray(shootingMethodsData) 
          ? shootingMethodsData 
          : shootingMethodsData 
            ? [shootingMethodsData] 
            : [];
        
        console.log('📸 Processing photographer shooting methods:', {
          photographerId: p?.userId || p?.user_id,
          rawData: {
            shootingMethods: p?.shootingMethods,
            shooting_methods: p?.shooting_methods
          },
          processed: shootingMethodsArray,
          isArray: Array.isArray(shootingMethodsArray),
          length: shootingMethodsArray.length
        });
        
        // SAFE MODE: Get service center coordinates and radius for distance-based filtering
        const serviceCenterLat = p?.serviceCenterLat || p?.service_center_lat || null;
        const serviceCenterLng = p?.serviceCenterLng || p?.service_center_lng || null;
        const serviceRadiusKm = p?.serviceRadiusKm || p?.service_radius_km || DEFAULT_SERVICE_RADIUS_KM;
        
        // Calculate distance from client to photographer (if both have coordinates)
        let distanceKm = null;
        let isWithinServiceRange = false;
        
        if (bookingData?.preciseLocation?.lat && bookingData?.preciseLocation?.lng && serviceCenterLat && serviceCenterLng) {
          distanceKm = calculateDistance(
            parseFloat(bookingData.preciseLocation.lat),
            parseFloat(bookingData.preciseLocation.lng),
            parseFloat(serviceCenterLat),
            parseFloat(serviceCenterLng)
          );
          isWithinServiceRange = distanceKm <= serviceRadiusKm;
          
          console.log('📏 Distance calculation for photographer:', {
            photographerId: p?.userId || p?.user_id,
            name: p?.user?.fullName || p?.user?.full_name,
            city: city,
            serviceCenterLat,
            serviceCenterLng,
            serviceRadiusKm,
            clientLat: bookingData.preciseLocation.lat,
            clientLng: bookingData.preciseLocation.lng,
            distanceKm: distanceKm?.toFixed(2),
            isWithinServiceRange
          });
        }
        
        return {
          id: p?.userId || p?.user_id,
          name: p?.user?.fullName || p?.user?.full_name || 'مصور',
          image: p?.user?.avatarUrl || p?.user?.avatar_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1bbd80f16-1763294584961.png',
          imageAlt: `صورة ${p?.user?.fullName || p?.user?.full_name || 'المصور'}`,
          avatar: p?.user?.avatarUrl || p?.user?.avatar_url || 'https://img.rocket.new/generatedImages/rocket_gen_img_1bbd80f16-1763294584961.png',
          avatarAlt: `صورة ${p?.user?.fullName || p?.user?.full_name || 'المصور'}`,
          rating: parseFloat(p?.rating) || 0,
          reviewCount: p?.totalReviews || p?.total_reviews || 0,
          price: 0, // Dynamic pricing - no fixed price
          priceUnit: 'ساعة',
          city: city,
          cityValue: cityValue,
          photographyTypes: p?.specializations || [],
          specialties: p?.specializations || [],
          experienceYears: experienceYears,
          experienceLevel: experienceLevelLabel, // Use calculated label from enum or years
          // Map database level to UI value for filtering
          experienceLevelValue: (() => {
            const dbToUi = { 'beginner': 'certified', 'intermediate': 'distinguished', 'advanced': 'distinguished', 'expert': 'elite' };
            if (experienceLevelEnum && dbToUi[experienceLevelEnum]) {
              return dbToUi[experienceLevelEnum];
            }
            return experienceYears >= 8 ? 'elite' : experienceYears >= 4 ? 'distinguished' : 'certified';
          })(),
          experience_level: experienceLevelEnum, // Keep original DB value for reference
          availability: (p?.availableForBooking || p?.available_for_booking) && (p?.status === 'approved' || !p?.status) ? 'available' : 'unavailable',
          equipmentAvailable: p?.equipmentAvailable || p?.equipment_available || [],
          equipment: p?.equipmentAvailable || p?.equipment_available || [],
          shootingMethods: shootingMethodsArray, // Ensure this is always an array
          portfolioImages: [],
          portfolioImage: null, // Will be loaded from portfolio
          portfolioImageAlt: 'صورة من معرض أعمال المصور',
          bio: p?.bio || '',
          matchScore: 0, // Will be calculated based on how well they match
          isVerified: p?.status === 'approved',
          // SAFE MODE: Service range data for filtering
          serviceCenterLat,
          serviceCenterLng,
          serviceRadiusKm,
          distanceKm,
          isWithinServiceRange,
          // التوفر الأسبوعي لفلترة النتائج حسب تاريخ الحجز
          weeklyAvailability: p?.weeklyAvailability ?? p?.weekly_availability ?? null
        };
      });
      
      // Load portfolio images for each photographer
      const photographersWithPortfolio = await Promise.all(
        formattedPhotographers.map(async (photographer) => {
          try {
            // Get featured image first, then most recent
            const { data: portfolioData, error: portfolioError } = await supabase
              .from('portfolio_images')
              .select('image_url, is_featured, created_at')
              .eq('photographer_id', photographer.id)
              .eq('approval_status', 'approved')
              .order('is_featured', { ascending: false })
              .order('created_at', { ascending: false })
              .limit(1);

            if (!portfolioError && portfolioData && portfolioData.length > 0) {
              return {
                ...photographer,
                portfolioImage: portfolioData[0].image_url,
                portfolioImageAlt: `صورة من معرض أعمال ${photographer.name}`,
                portfolioImages: portfolioData.map(img => img.image_url)
              };
            }
          } catch (err) {
            console.warn(`Failed to load portfolio for photographer ${photographer.id}:`, err);
          }
          
          // Fallback to default image if no portfolio
          return {
            ...photographer,
            portfolioImage: null,
            portfolioImageAlt: 'صورة افتراضية',
            portfolioImages: []
          };
        })
      );
      
      setPhotographers(photographersWithPortfolio);
    } catch (err) {
      console.error('Failed to load photographers:', err);
      setError(err?.message || 'فشل تحميل المصورين');
    } finally {
      setLoading(false);
    }
  };

  // Map photography type from booking wizard to specializations
  const mapPhotographyTypeToSpecialization = (type) => {
    const typeMap = {
      'wedding': 'حفلات الزفاف',
      'portrait': 'التصوير الشخصي',
      'event': 'تصوير المناسبات',
      'commercial': 'التصوير التجاري',
      'real-estate': 'تصوير العقارات',
      'food': 'تصوير الطعام',
      'fashion': 'تصوير الأزياء',
      'sports': 'التصوير الرياضي',
      'product': 'تصوير المنتجات',
    };
    return typeMap[type] || type;
  };

  // Map experience level to years range
  const mapExperienceLevelToYears = (level) => {
    const levelMap = {
      'certified': { min: 0, max: 3 },
      'distinguished': { min: 4, max: 7 },
      'elite': { min: 8, max: 999 },
      // Legacy
      'beginner': { min: 0, max: 3 },
      'intermediate': { min: 4, max: 7 },
      'advanced': { min: 4, max: 7 },
      'expert': { min: 8, max: 999 }
    };
    return levelMap[level] || { min: 0, max: 999 };
  };

  // خيارات التعديل التي يقدر عليها جميع المصورين — لا تُستخدم للفلترة الصارمة
  const UNIVERSAL_EDITING_OPTIONS = ['basic-editing', 'no-editing'];

  // Map equipment from booking wizard to equipment labels (Arabic labels as stored in DB)
  const mapEquipmentToLabel = (equipment) => {
    const equipmentMap = {
      'drone':                'طائرة مسيرة (Drone)',
      'lighting':             'إضاءة الاستوديو',
      'video':                'كاميرا فيديو',
      'backdrop':             'خلفيات تصوير',
      'assistant':            'مساعد مصور',
      'makeup':               'خدمة المكياج',
      'basic-editing':        'تعديل أساسي (تصحيح إضاءة وألوان)',
      'professional-editing': 'تعديل احترافي متقدم',
      'no-editing':           'صور أصلية بدون تعديل',
    };
    return equipmentMap[equipment] || equipment;
  };

  const filterPhotographers = (list) => {
    return list?.map((photographer) => {
      // Calculate match score (points)
      let matchScorePoints = 0;
      let maxPossiblePoints = 0;

      // City match (10 points)
      maxPossiblePoints += 10;
      const cityMatch = !bookingData?.city || 
        photographer?.city?.toLowerCase() === bookingData?.city?.toLowerCase() ||
        photographer?.cityValue === bookingData?.city;
      if (cityMatch && bookingData?.city) matchScorePoints += 10;

      // Photography type match (30 points)
      maxPossiblePoints += 30;
      let typeMatch = true;
      if (bookingData?.photographyType) {
        const requestedSpecialization = mapPhotographyTypeToSpecialization(bookingData.photographyType);
        const photographerSpecializations = photographer?.photographyTypes || [];
        typeMatch = photographerSpecializations.some(spec => 
          spec === requestedSpecialization || 
          spec?.toLowerCase() === requestedSpecialization?.toLowerCase() ||
          spec?.includes(requestedSpecialization) ||
          requestedSpecialization?.includes(spec)
        );
        if (typeMatch) matchScorePoints += 30;
      }

      // Experience level match (20 points)
      maxPossiblePoints += 20;
      let experienceMatch = true;
      if (bookingData?.experienceLevel) {
        // Map database experience_level to UI values for comparison
        const dbToUiLevelMap = {
          'beginner': 'certified',
          'intermediate': 'distinguished',
          'advanced': 'distinguished',
          'expert': 'elite',
          'certified': 'certified',
          'distinguished': 'distinguished',
          'elite': 'elite',
        };
        
        const photographerLevel = photographer?.experienceLevelValue 
          || dbToUiLevelMap[photographer?.experience_level] 
          || 'certified';
        
        const levelOrder = { 'certified': 1, 'distinguished': 2, 'elite': 3 };
        const requestedOrder = levelOrder[bookingData.experienceLevel] || 1;
        const photographerOrder = levelOrder[photographerLevel] || 1;
        
        // Photographer qualifies if their level is >= requested level
        experienceMatch = photographerOrder >= requestedOrder;
        if (experienceMatch) matchScorePoints += 20;
      }

      // Equipment match (20 points - more equipment = higher score)
      // Universal editing options (basic/no-editing) are skipped — all photographers qualify
      maxPossiblePoints += 20;
      let equipmentMatch = true;
      let equipmentScore = 0;
      const specialEquipment = (bookingData?.equipment || []).filter(eq => !UNIVERSAL_EDITING_OPTIONS.includes(eq));
      if (specialEquipment.length > 0) {
        const photographerEquipment = photographer?.equipmentAvailable || photographer?.equipment || [];
        // Map requested equipment to labels
        const requestedEquipmentLabels = specialEquipment.map(eq => mapEquipmentToLabel(eq));
        const matchedEquipment = requestedEquipmentLabels.filter(reqEquip => 
          photographerEquipment.some(photEquip => 
            photEquip?.toLowerCase() === reqEquip?.toLowerCase() ||
            photEquip?.includes(reqEquip) ||
            reqEquip?.includes(photEquip) ||
            photEquip?.toLowerCase().includes(reqEquip?.toLowerCase()) ||
            reqEquip?.toLowerCase().includes(photEquip?.toLowerCase())
          )
        );
        equipmentMatch = matchedEquipment.length > 0;
        if (equipmentMatch) {
          equipmentScore = (matchedEquipment.length / specialEquipment.length) * 20;
          matchScorePoints += equipmentScore;
        }
      } else {
        equipmentMatch = true; // No special equipment requirement
        matchScorePoints += 20; // Full points if no requirement
      }

      // Shooting method match (25 points - critical filter)
      // Uses AND logic: photographer must support ALL methods requested by customer
      maxPossiblePoints += 25;
      let shootingMethodMatch = true;
      const requestedMethods = Array.isArray(bookingData?.shootingMethods) && bookingData.shootingMethods.length > 0
        ? bookingData.shootingMethods
        : bookingData?.shootingMethod ? [bookingData.shootingMethod] : [];

      if (requestedMethods.length > 0) {
        const photographerMethodsArr = Array.isArray(photographer?.shootingMethods)
          ? photographer.shootingMethods
          : photographer?.shootingMethods ? [photographer.shootingMethods] : [];

        shootingMethodMatch = requestedMethods.every(m => photographerMethodsArr.includes(m));

        console.log('🎯 Shooting method match check:', {
          requested: requestedMethods,
          photographerMethods: photographerMethodsArr,
          match: shootingMethodMatch,
          photographerId: photographer?.id
        });

        if (shootingMethodMatch) {
          matchScorePoints += 25;
        }
      } else {
        shootingMethodMatch = true;
        matchScorePoints += 25;
      }

      // Rating bonus (10 points max - rating is 0-5, so 5*2 = 10)
      maxPossiblePoints += 10;
      const ratingBonus = Math.min((photographer?.rating || 0) * 2, 10);
      matchScorePoints += ratingBonus;

      // Distance bonus (if precise location is available) - 10 points max
      if (bookingData?.preciseLocation?.lat && bookingData?.preciseLocation?.lng && photographer?.distanceKm !== undefined) {
        maxPossiblePoints += 10;
        // Closer photographers get higher score (up to 10 points)
        const distanceBonus = Math.max(0, Math.min(10, 10 - (photographer.distanceKm || 0) / 2));
        matchScorePoints += distanceBonus;
      }

      // Availability (10 points)
      maxPossiblePoints += 10;
      const isAvailable = photographer?.availability === 'available';
      if (isAvailable) matchScorePoints += 10;

      // Convert points to percentage (0-100%)
      const matchScorePercentage = maxPossiblePoints > 0 
        ? Math.min(100, Math.round((matchScorePoints / maxPossiblePoints) * 100))
        : 0;

      return {
        ...photographer,
        matchScore: matchScorePercentage
      };
    }).filter((photographer) => {
      // SAFE MODE: Filter by service range (distance) instead of city
      // Photographers are shown if client's location is within their service range
      let serviceRangeMatch = true;
      
      if (bookingData?.preciseLocation?.lat && bookingData?.preciseLocation?.lng) {
        // If we have client's precise location, use distance-based filtering
        if (photographer.serviceCenterLat && photographer.serviceCenterLng) {
          // Photographer has service center - use distance check
          serviceRangeMatch = photographer.isWithinServiceRange === true;
        } else {
          // Photographer has no service center coordinates - fallback to city match
          serviceRangeMatch = !bookingData?.city || 
            photographer?.city?.toLowerCase() === bookingData?.city?.toLowerCase() ||
            photographer?.cityValue === bookingData?.city;
        }
      } else {
        // No precise location - fallback to city-based filtering
        serviceRangeMatch = !bookingData?.city || 
          photographer?.city?.toLowerCase() === bookingData?.city?.toLowerCase() ||
          photographer?.cityValue === bookingData?.city;
      }
      
      console.log('🔍 Service range filter for:', {
        photographerId: photographer.id,
        name: photographer.name,
        city: photographer.city,
        distanceKm: photographer.distanceKm?.toFixed(2),
        serviceRadiusKm: photographer.serviceRadiusKm,
        isWithinServiceRange: photographer.isWithinServiceRange,
        serviceRangeMatch,
        hasPreciseLocation: !!bookingData?.preciseLocation?.lat
      });

      let typeMatch = true;
      if (bookingData?.photographyType) {
        const requestedSpecialization = mapPhotographyTypeToSpecialization(bookingData.photographyType);
        const photographerSpecializations = photographer?.photographyTypes || [];
        typeMatch = photographerSpecializations.some(spec => 
          spec === requestedSpecialization || 
          spec?.toLowerCase() === requestedSpecialization?.toLowerCase() ||
          spec?.includes(requestedSpecialization) ||
          requestedSpecialization?.includes(spec)
        );
      }

      let experienceMatch = true;
      if (bookingData?.experienceLevel) {
        // Map database experience_level to UI values for comparison
        const dbToUiLevelMap = {
          'beginner': 'certified',
          'intermediate': 'distinguished',
          'advanced': 'distinguished',
          'expert': 'elite',
          // UI values map to themselves
          'certified': 'certified',
          'distinguished': 'distinguished',
          'elite': 'elite',
        };
        
        // Get photographer's level from experienceLevelValue (UI value) or map from DB value
        const photographerLevel = photographer?.experienceLevelValue 
          || dbToUiLevelMap[photographer?.experience_level] 
          || 'certified';
        
        // Match if photographer level is same or higher than requested
        const levelOrder = { 'certified': 1, 'distinguished': 2, 'elite': 3 };
        const requestedOrder = levelOrder[bookingData.experienceLevel] || 1;
        const photographerOrder = levelOrder[photographerLevel] || 1;
        
        // Photographer qualifies if their level is >= requested level
        experienceMatch = photographerOrder >= requestedOrder;
        
        console.log('🎯 Experience level match:', {
          requested: bookingData.experienceLevel,
          photographerLevel,
          photographerDbLevel: photographer?.experience_level,
          match: experienceMatch
        });
      }

      // Universal editing options (basic/no-editing) don't restrict results — skip them in hard filter
      let equipmentMatch = true;
      const specialEquipmentFilter = (bookingData?.equipment || []).filter(eq => !UNIVERSAL_EDITING_OPTIONS.includes(eq));
      if (specialEquipmentFilter.length > 0) {
        const photographerEquipment = photographer?.equipmentAvailable || photographer?.equipment || [];
        // Map requested equipment to labels
        const requestedEquipmentLabels = specialEquipmentFilter.map(eq => mapEquipmentToLabel(eq));
        equipmentMatch = requestedEquipmentLabels.some(reqEquip => 
          photographerEquipment.some(photEquip => 
            photEquip?.toLowerCase() === reqEquip?.toLowerCase() ||
            photEquip?.includes(reqEquip) ||
            reqEquip?.includes(photEquip) ||
            photEquip?.toLowerCase().includes(reqEquip?.toLowerCase()) ||
            reqEquip?.toLowerCase().includes(photEquip?.toLowerCase())
          )
        );
      }

      // Shooting method filter (critical - AND logic: photographer must support ALL requested methods)
      const _reqMethods = Array.isArray(bookingData?.shootingMethods) && bookingData.shootingMethods.length > 0
        ? bookingData.shootingMethods
        : bookingData?.shootingMethod ? [bookingData.shootingMethod] : [];
      let shootingMethodMatch = true;
      if (_reqMethods.length > 0) {
        const _photMethods = Array.isArray(photographer?.shootingMethods)
          ? photographer.shootingMethods
          : photographer?.shootingMethods ? [photographer.shootingMethods] : [];

        shootingMethodMatch = _reqMethods.every(m => _photMethods.includes(m));

        console.log('🔍 Shooting method filter:', {
          requested: _reqMethods,
          photographerMethods: _photMethods,
          match: shootingMethodMatch,
          photographerId: photographer?.id,
          willShow: shootingMethodMatch
        });
      }

      const priceMatch = photographer?.price >= priceFilter?.min && photographer?.price <= priceFilter?.max;
      const ratingMatch = photographer?.rating >= ratingFilter;
      const availabilityMatch = availabilityFilter === 'all' || photographer?.availability === availabilityFilter;

      // فلترة حسب التوفر الأسبوعي: استبعاد المصورين غير المتاحين في يوم ووقت الحجز (SAFE: إن لم يوجد تاريخ/وقت أو توفر نعرض المصور)
      let weeklyAvailabilityMatch = true;
      const bookingDate = bookingData?.date || bookingData?.bookingDate;
      const bookingTime = bookingData?.time || bookingData?.bookingTime;
      const weekly = photographer?.weeklyAvailability ?? photographer?.weekly_availability;
      if (bookingDate && bookingTime && weekly && typeof weekly === 'object' && Object.keys(weekly).length > 0) {
        const d = new Date(bookingDate + 'T12:00:00');
        const dayName = ARABIC_DAYS[d.getDay()];
        const daySchedule = weekly[dayName];
        // SAFE: إن لم يكن اليوم في التوفر = استبعاد (جدول ناقص أو اليوم غير مضاف)
        if (!daySchedule) {
          weeklyAvailabilityMatch = false;
        } else {
          const [h] = String(bookingTime).split(':').map(Number);
          const period = getPeriodForHour(h);
          if (period) {
            weeklyAvailabilityMatch = daySchedule[period] === true;
          }
        }
      }

      // SAFE MODE: Use serviceRangeMatch instead of cityMatch
      return serviceRangeMatch && typeMatch && experienceMatch && equipmentMatch && shootingMethodMatch && priceMatch && ratingMatch && availabilityMatch && weeklyAvailabilityMatch;
    });
  };

  const sortPhotographers = (list) => {
    const sorted = [...list];
    switch (sortBy) {
      case 'relevance':
        // SAFE MODE: When user has precise location, factor in distance to relevance
        return sorted?.sort((a, b) => {
          // First by match score
          const scoreDiff = (b?.matchScore || 0) - (a?.matchScore || 0);
          if (Math.abs(scoreDiff) > 10) return scoreDiff; // Big score difference, use it
          
          // If scores are close, prefer closer photographers
          if (a?.distanceKm !== undefined && b?.distanceKm !== undefined) {
            return a.distanceKm - b.distanceKm;
          }
          return scoreDiff;
        });
      case 'price-low':
        return sorted?.sort((a, b) => a?.price - b?.price);
      case 'price-high':
        return sorted?.sort((a, b) => b?.price - a?.price);
      case 'rating':
        return sorted?.sort((a, b) => b?.rating - a?.rating);
      case 'distance':
        // Sort by distance (closest first)
        return sorted?.sort((a, b) => {
          if (a?.distanceKm !== undefined && b?.distanceKm !== undefined) {
            return a.distanceKm - b.distanceKm;
          }
          if (a?.distanceKm !== undefined) return -1;
          if (b?.distanceKm !== undefined) return 1;
          return (b?.matchScore || 0) - (a?.matchScore || 0);
        });
      default:
        return sorted;
    }
  };

  const handleBookNow = async (photographerId) => {
    try {
      // Find photographer info
      const photographer = photographers.find(p => (p.id || p.userId) === photographerId);
      
      // Get photographer service data
      const serviceCenterLat = photographer?.serviceCenterLat || photographer?.service_center_lat;
      const serviceCenterLng = photographer?.serviceCenterLng || photographer?.service_center_lng;
      const serviceRadiusKm = photographer?.serviceRadiusKm || photographer?.service_radius_km || 20;
      
      // Get user's precise location from booking data
      const userLat = bookingData?.preciseLocation?.lat;
      const userLng = bookingData?.preciseLocation?.lng;
      
      // SAFE MODE: Validate service range if both locations are available
      let isOutsideRange = false;
      let distanceKm = null;
      
      if (serviceCenterLat && serviceCenterLng && userLat && userLng) {
        distanceKm = calculateDistance(
          parseFloat(userLat),
          parseFloat(userLng),
          parseFloat(serviceCenterLat),
          parseFloat(serviceCenterLng)
        );
        isOutsideRange = distanceKm > serviceRadiusKm;
        
        console.log('📏 Service range check:', {
          userLocation: { lat: userLat, lng: userLng },
          photographerLocation: { lat: serviceCenterLat, lng: serviceCenterLng },
          distanceKm: distanceKm?.toFixed(1),
          serviceRadiusKm,
          isOutsideRange
        });
      }
      
      // Combine booking data with selected photographer (include avatar for checkout and customer bookings)
      const checkoutData = {
        ...bookingData,
        photographerId: photographerId,
        photographerName: photographer?.name || 'مصور',
        photographerAvatarUrl: photographer?.avatar || photographer?.user?.avatar_url || photographer?.user?.avatarUrl || null,
        distanceFromPhotographerKm: distanceKm,
        isOutsideServiceRange: isOutsideRange
      };

      // SAFE MODE: If outside range, show modal instead of blocking
      if (isOutsideRange) {
        setOutOfRangePhotographer({
          ...photographer,
          serviceRadiusKm
        });
        setCalculatedDistance(distanceKm);
        setShowOutOfRangeModal(true);
        // Still save checkout data for later use
        updateDraft(checkoutData);
        return;
      }

      // Save to draft
      updateDraft(checkoutData);

      // Navigate to checkout page
      navigate('/checkout', {
        state: { bookingData: checkoutData }
      });
    } catch (err) {
      console.error('Failed to navigate to checkout:', err);
      setError('فشل الانتقال إلى صفحة الدفع. يرجى المحاولة مرة أخرى.');
    }
  };
  
  // SAFE MODE: Handle exception request from out-of-range modal (keep photographer avatar if present)
  const handleRequestException = () => {
    const checkoutData = {
      ...draft,
      exceptionRequested: true
    };
    updateDraft(checkoutData);
    setShowOutOfRangeModal(false);
    navigate('/checkout', {
      state: { bookingData: checkoutData }
    });
  };
  
  // SAFE MODE: Handle finding closer photographers
  const handleFindCloserPhotographers = () => {
    setShowOutOfRangeModal(false);
    // Scroll to top to see the filtered list
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // SAFE MODE: Handle changing location
  const handleChangeLocation = () => {
    setShowOutOfRangeModal(false);
    // Navigate back to booking wizard to change location
    navigate('/booking-wizard?step=6&return=photographer-results');
  };

  const handleViewProfile = (photographerId) => {
    setSelectedPhotographerId(photographerId);
    setShowProfileModal(true);
  };

  const handleCloseProfileModal = () => {
    setShowProfileModal(false);
    setSelectedPhotographerId(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="pt-20 pb-20">
          <div className="container mx-auto px-4 md:px-6 lg:px-8">
            {/* Skeleton Header */}
            <div className="mb-8 animate-pulse">
              <div className="h-8 bg-slate-200 rounded-xl w-56 mb-3" />
              <div className="h-5 bg-slate-200 rounded-xl w-40" />
            </div>
            {/* Skeleton Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 animate-pulse">
                  {/* صورة المحفظة */}
                  <div className="h-72 bg-slate-200" />
                  {/* بيانات المصور */}
                  <div className="p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-slate-200 rounded w-3/4" />
                        <div className="h-3 bg-slate-200 rounded w-1/2" />
                      </div>
                    </div>
                    <div className="h-3 bg-slate-200 rounded w-full" />
                    <div className="h-3 bg-slate-200 rounded w-5/6" />
                    <div className="flex justify-between items-center pt-1">
                      <div className="h-6 bg-slate-200 rounded w-20" />
                      <div className="h-9 bg-slate-200 rounded-xl w-24" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filteredPhotographers = filterPhotographers(photographers);
  const sortedPhotographers = sortPhotographers(filteredPhotographers);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="pt-20 pb-20">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="mb-4">
            <BackButton to="/booking-wizard" label="العودة إلى خطوات الحجز" />
          </div>
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
              المصورون المتاحون
            </h1>
            <p className="text-lg text-slate-600">
              وجدنا {filteredPhotographers?.length} مصور محترف يطابق احتياجاتك
            </p>
          </div>

          {/* Filters */}
          <FilterControls
            sortBy={sortBy}
            onSortChange={setSortBy}
            priceFilter={priceFilter}
            onPriceFilterChange={setPriceFilter}
            ratingFilter={ratingFilter}
            onRatingFilterChange={setRatingFilter}
            availabilityFilter={availabilityFilter}
            onAvailabilityFilterChange={setAvailabilityFilter}
            onResetFilters={() => {
              setPriceFilter({ min: 0, max: 10000 });
              setRatingFilter(0);
              setAvailabilityFilter('all');
              setSortBy('relevance');
            }}
          />

          {/* Results Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedPhotographers?.map((photographer) => (
              <PhotographerResultCard
                key={photographer?.id}
                photographer={photographer}
                onViewProfile={handleViewProfile}
                onBookNow={handleBookNow}
              />
            ))}
          </div>

          {filteredPhotographers?.length === 0 && (
            <div className="text-center py-20">
              <Icon name="Search" size={64} className="text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                لم نجد نتائج مطابقة
              </h3>
              <p className="text-slate-600 mb-6">
                جرب تعديل معايير البحث أو الفلاتر
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/booking-wizard')}
              >
                ابدأ بحث جديد
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Photographer Profile Modal */}
      <PhotographerProfileModal
        isOpen={showProfileModal}
        onClose={handleCloseProfileModal}
        photographerId={selectedPhotographerId}
      />
      
      {/* SAFE MODE: Out of Range Modal */}
      {showOutOfRangeModal && outOfRangePhotographer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" dir="rtl">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertTriangle" size={32} className="text-amber-600" />
            </div>
            
            <h3 className="text-xl font-bold text-slate-900 mb-2">
              موقع التصوير خارج نطاق الخدمة
            </h3>
            
            <p className="text-slate-600 mb-4">
              الموقع المحدد يبعد <strong className="text-amber-600">{calculatedDistance?.toFixed(1)} كم</strong> عن موقع المصور
              <br />
              <span className="text-sm">({outOfRangePhotographer?.name})</span>
              <br />
              نطاق خدمة المصور: <strong>{outOfRangePhotographer?.serviceRadiusKm} كم</strong>
            </p>
            
            <div className="space-y-3">
              <Button
                variant="default"
                fullWidth
                onClick={handleRequestException}
                iconName="MessageSquare"
                iconPosition="right"
              >
                اطلب استثناء من المصور
              </Button>
              
              <Button
                variant="outline"
                fullWidth
                onClick={handleChangeLocation}
                iconName="MapPin"
                iconPosition="right"
              >
                تغيير موقع التصوير
              </Button>
              
              <Button
                variant="ghost"
                fullWidth
                onClick={handleFindCloserPhotographers}
                iconName="Search"
                iconPosition="right"
                className="text-slate-500"
              >
                اختر مصوراً آخر
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

};

export default PhotographerResults;